const AUTOMATIC_GIFT_PROPERTY_KEY = '_automatic_gift';
const AUTOMATIC_GIFT_PROPERTY_VALUE = 'true';

class AutomaticGift {
  constructor() {
    this.isSyncing = false;
    this.hasInitialized = false;
    this.hasPendingSync = false;
    this.deferUntilLuxuryDecision = false;
    this.addedToCartResyncTimer = null;
  }

  init() {
    if (this.hasInitialized) return;

    this.hasInitialized = true;

    this.syncCartGift();

    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event?.source === 'luxury-gift-drawer') return;

      if (this.deferUntilLuxuryDecision) return;

      this.syncCartGift();
    });

    document.body.addEventListener('addedToCart', (event) => {
      if (event?.detail?.source === 'luxury-gift-drawer') return;

      const luxuryDrawer = document.querySelector('luxury-gift-drawer');

      if (luxuryDrawer?.classList.contains('drawer-open')) {
        this.deferUntilLuxuryDecision = true;

        return;
      }

      this.syncCartGift();

      if (this.addedToCartResyncTimer) {
        window.clearTimeout(this.addedToCartResyncTimer);
      }

      // First add can race with /cart.js propagation; run one delayed sync to catch it.
      this.addedToCartResyncTimer = window.setTimeout(() => {
        this.addedToCartResyncTimer = null;

        if (this.deferUntilLuxuryDecision) return;

        this.syncCartGift();
      }, 350);
    });

    document.body.addEventListener('luxuryGiftDecision', () => {
      this.deferUntilLuxuryDecision = false;
      this.syncCartGift();
    });
  }

  async syncCartGift() {
    if (this.isSyncing) {
      this.hasPendingSync = true;

      return;
    }

    this.isSyncing = true;
    this.hasPendingSync = false;

    try {
      const automaticGiftSettings = window.ThatWorks?.shopSettings?.automaticGift;
      const giftVariantId = Number(automaticGiftSettings?.variantId);
      const isEnabled = Boolean(automaticGiftSettings?.enabled);
      const hasValidGiftVariant = Number.isFinite(giftVariantId) && giftVariantId > 0;
      const cart = await this.fetchCart();

      if (!cart) return;

      const cartItems = cart.items || [];
      const configuredAutomaticGiftLines = this.getAutomaticGiftLines(cartItems, giftVariantId);
      const nonGiftItemsCount = this.getNonGiftItemsCount(cart.items || []);

      if (!isEnabled || !hasValidGiftVariant || nonGiftItemsCount === 0) {
        const giftCleanupLines = this.getGiftCleanupLines(cartItems, giftVariantId);

        await this.removeGiftLines(giftCleanupLines);

        return;
      }

      if (configuredAutomaticGiftLines.length === 0) {
        await this.addGiftLine(giftVariantId);

        return;
      }

      if (configuredAutomaticGiftLines[0].quantity !== 1) {
        await this.updateLineQuantity(configuredAutomaticGiftLines[0].key, 1);
        this.notifyCartUpdated();
      }
    } catch (error) {
      console.error('automatic-gift:', error);
    } finally {
      this.isSyncing = false;

      if (this.hasPendingSync) {
        this.hasPendingSync = false;

        this.syncCartGift();
      }
    }
  }

  async fetchCart() {
    const response = await fetch(`${ThatWorks.shopSettings.routes.cart_url}.js`);

    if (!response.ok) return null;

    return response.json();
  }

  getAutomaticGiftLines(items, giftVariantId = null) {
    const lines = [];

    for (const [index, item] of items.entries()) {
      const isAutomaticGift =
        item.properties && item.properties[AUTOMATIC_GIFT_PROPERTY_KEY] === AUTOMATIC_GIFT_PROPERTY_VALUE;

      if (!isAutomaticGift) continue;

      if (giftVariantId !== null && Number(item.variant_id) !== giftVariantId) continue;

      lines.push({
        line: index + 1,
        key: item.key,
        quantity: item.quantity,
      });
    }

    return lines;
  }

  getGiftCleanupLines(items, giftVariantId) {
    // A shared variant can also be purchased or managed by a gift app.
    // Only clean up lines owned by this theme's automatic-gift feature.
    return this.getAutomaticGiftLines(items);
  }

  getNonGiftItemsCount(items) {
    let total = 0;

    for (const item of items) {
      const isAutomaticGift =
        item.properties && item.properties[AUTOMATIC_GIFT_PROPERTY_KEY] === AUTOMATIC_GIFT_PROPERTY_VALUE;

      if (isAutomaticGift) continue;

      total += item.quantity;
    }

    return total;
  }

  async addGiftLine(giftVariantId) {
    const payload = {
      items: [
        {
          id: giftVariantId,
          quantity: 1,
          properties: {
            [AUTOMATIC_GIFT_PROPERTY_KEY]: AUTOMATIC_GIFT_PROPERTY_VALUE,
          },
        },
      ],
    };

    const response = await fetch(`${ThatWorks.shopSettings.routes.cart_add_url}.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const message = await response.text();

      throw new Error(message || 'Failed to add automatic gift');
    }

    this.notifyCartUpdated();
  }

  async removeGiftLines(lines) {
    if (lines.length === 0) return;

    for (const line of [...lines].sort((a, b) => b.line - a.line)) {
      await this.updateLineQuantity(line.key, 0);
    }

    this.notifyCartUpdated();
  }

  async updateLineQuantity(key, quantity) {
    const response = await fetch(`${ThatWorks.shopSettings.routes.cart_change_url}.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        id: key,
        quantity,
      }),
    });

    if (!response.ok) {
      const message = await response.text();

      throw new Error(message || 'Failed to update automatic gift line');
    }
  }

  notifyCartUpdated() {
    publish(PUB_SUB_EVENTS.cartUpdate, { source: 'automatic-gift' });
    this.refreshCartIconBubble();
  }

  async refreshCartIconBubble() {
    const target = document.getElementById('cart-icon-bubble');

    if (!target) return;

    try {
      const response = await fetch(`${ThatWorks.shopSettings.routes.cart_url}?section_id=cart-icon-bubble`);

      if (!response.ok) return;

      const htmlText = await response.text();
      const html = new DOMParser().parseFromString(htmlText, 'text/html');
      const sourceById = html.getElementById('cart-icon-bubble');

      if (sourceById) {
        target.innerHTML = sourceById.innerHTML;

        return;
      }

      const sectionRoot = html.querySelector('.shopify-section');

      if (!sectionRoot) return;

      target.innerHTML = sectionRoot.innerHTML;
    } catch (error) {
      console.error('automatic-gift bubble refresh:', error);
    }
  }
}

const automaticGift = new AutomaticGift();

document.addEventListener('DOMContentLoaded', () => {
  automaticGift.init();
});
